/**
 * The theme samples of the Release Kit: one short muted loop and one poster per release theme, made
 * by the real renderer from the demo song "Swamps" by LaziiBee
 * (`music24-api/src/scripts/renderLookSamples.ts`), and one lyric card still per theme and lyric
 * card style, from the demo song "Forests". The web app serves them as static files. The
 * file has no runtime imports, so every project can use it.
 */
import type { AssetStyleId } from './assetStyles';
import type { KitFontId, ReleaseThemeId } from './releaseThemes';

/** The folder of the samples under the web app's `public/`. */
export const LOOK_SAMPLES_PATH = 'statics/release-kit/themes';
/** The folder of the kit fonts (WOFF2 copies of the renderer fonts) under the web app's `public/`. */
export const KIT_FONTS_WEB_PATH = 'statics/fonts/kit';

/** The length of one sample loop. */
export const LOOK_SAMPLE_SEC = 4;
/** The pixel size of a sample (4:5, the shape of a feed post). */
export const LOOK_SAMPLE_SIZE = { width: 480, height: 600 } as const;

/** The demo song of the samples. Music24 owns LaziiBee and holds the rights. */
export const LOOK_SAMPLE_SONG = { title: 'Swamps', artistName: 'LaziiBee', kicker: 'Out now' } as const;

/**
 * The lyric card samples (owner decision 2026-10-05): one still per theme and lyric card style,
 * with a chorus line of the demo song "Forests" by LaziiBee on the Forests cover. The line is quoted
 * from `music24-api/fixtures/masters/forests.txt` with no change.
 */
export const LYRIC_SAMPLE_SONG = {
  title: 'Forests',
  artistName: 'LaziiBee',
  line: 'LaziBee, take me to the Forests',
} as const;

/**
 * The styles of a lyric card (`ASSET_TYPE_STYLES.lyricCard` in `assetStyles.ts`, design q: "the
 * lyric card offers Cover ground and Type only"). A test keeps the two lists equal.
 */
export const LYRIC_SAMPLE_STYLES = ['coverGround', 'typeOnly'] as const satisfies readonly AssetStyleId[];
export type LyricSampleStyle = (typeof LYRIC_SAMPLE_STYLES)[number];

export function isLyricSampleStyle(value: unknown): value is LyricSampleStyle {
  return (LYRIC_SAMPLE_STYLES as readonly unknown[]).includes(value);
}

/** The pixel size of a lyric card sample: square, the shape of the lyric card file (`lyricCard11`, 1080 x 1080). */
export const LYRIC_SAMPLE_SIZE = { width: 480, height: 480 } as const;

/** The file name of the sample of a theme, without the extension. */
export function themeSampleBaseName(themeId: ReleaseThemeId): string {
  return themeId;
}

/** The file name of the lyric card sample of a theme in a style, without the extension. */
export function lyricSampleBaseName(themeId: ReleaseThemeId, style: LyricSampleStyle): string {
  return `lyric-${themeId}-${style}`;
}

/** The files of the samples of a theme, relative to `public/`: the loop, its poster, and one lyric card still per style. */
export interface ThemeSampleFiles {
  video: string;
  poster: string;
  lyricCards: Readonly<Record<LyricSampleStyle, string>>;
}

/** The files of the samples of a theme, relative to `public/`. */
export function themeSampleFiles(themeId: ReleaseThemeId): ThemeSampleFiles {
  const base = `${LOOK_SAMPLES_PATH}/${themeSampleBaseName(themeId)}`;
  return {
    video: `${base}.mp4`,
    poster: `${base}.jpg`,
    lyricCards: {
      coverGround: `${LOOK_SAMPLES_PATH}/${lyricSampleBaseName(themeId, 'coverGround')}.jpg`,
      typeOnly: `${LOOK_SAMPLES_PATH}/${lyricSampleBaseName(themeId, 'typeOnly')}.jpg`,
    },
  };
}

/** The WOFF2 file of a kit font, relative to `public/`. */
export function kitFontWebFile(fontId: KitFontId): string {
  return `${KIT_FONTS_WEB_PATH}/${fontId}.woff2`;
}
